import api from "./api";

export type RegistroParametrizacao = {
  id: number;
  nome: string;
  descricao?: string | null;
  ativo?: boolean | number | null;
  tipo?: string | null;
  superintendencia_id?: number | null;
  grupo_id?: number | null;
  [key: string]: unknown;
};

export type PoloRegionalAdmin = {
  id: number;
  nome: string;
  cidade_sede?: string | null;
  ativo?: boolean | number | null;
  municipios?: Array<{ id?: number; city_id?: number; nome?: string }>;
  cidades?: Array<{ id?: number; city_id?: number; nome?: string }>;
};

export type FiltroRelacionamentoCampo = {
  field: string;
  operator: string;
  value?: string | number | boolean;
  source_field?: string;
};

export type RelacionamentoCampoAdmin = {
  id: number;
  categoria: string;
  categoria_label?: string;
  versao_id?: number;
  versao_numero?: number;
  versao_nome?: string | null;
  versao_status?: RelacionamentoVersaoStatus;
  source_table?: string | null;
  source_field: string;
  source_value_id: number | null;
  aplicar_para_todos?: boolean | number | null;
  target_field: string;
  target_label: string;
  options_table: string;
  options_filters?: FiltroRelacionamentoCampo[];
  ordem?: number | null;
  obrigatorio?: boolean | number | null;
  ativo?: boolean | number | null;
  created_at?: string;
  updated_at?: string;
};

export type RelacionamentoVersaoStatus = "RASCUNHO" | "PUBLICADA" | "ARQUIVADA";

export type RelacionamentoVersao = {
  id: number;
  categoria: string;
  categoria_label?: string;
  numero: number;
  nome?: string | null;
  status: RelacionamentoVersaoStatus;
  regras_count?: number;
  publicado_at?: string | null;
  publicado_by?: number | null;
  publicado_por_nome?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type RelacionamentoCampoPayload = {
  versao_id: number;
  source_field: string;
  source_value_id?: number | null;
  aplicar_para_todos: boolean;
  target_field: string;
  target_label: string;
  options_table: string;
  options_filters?: FiltroRelacionamentoCampo[];
  ordem?: number | null;
  obrigatorio?: boolean;
  ativo?: boolean;
};

const authHeaders = () => {
  const token = sessionStorage.getItem("authToken");
  return token ? { Authorization: `Bearer ${token}` } : undefined;
};

const extractData = <T>(payload: unknown): T[] => {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === "object" && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: T[] }).data;
  }
  return [];
};

export const listarCamposAdmin = async (basePath: "dados_complementares" | "acoes", table: string) => {
  const response = await api.get(`/${basePath}/admin/${table}`, { headers: authHeaders() });
  return extractData<RegistroParametrizacao>(response.data);
};

export const listarRelacionamentosCamposAdmin = async (versaoId: number) => {
  const response = await api.get("/dados_complementares/admin/relacionamentos-campos", {
    headers: authHeaders(),
    params: { versao_id: versaoId },
  });
  return extractData<RelacionamentoCampoAdmin>(response.data);
};

export const listarVersoesRelacionamentosCamposAdmin = async (params?: Record<string, string | undefined>) => {
  const response = await api.get("/dados_complementares/admin/relacionamentos-campos/versoes", {
    headers: authHeaders(),
    params,
  });
  return extractData<RelacionamentoVersao>(response.data);
};

export const criarVersaoRelacionamentosCamposAdmin = async (payload: {
  categoria: string;
  nome?: string;
  copiar_de_versao_id?: number;
}) => {
  const response = await api.post("/dados_complementares/admin/relacionamentos-campos/versoes", payload, { headers: authHeaders() });
  return response.data;
};

export const atualizarVersaoRelacionamentosCamposAdmin = async (id: number, payload: { nome: string }) => {
  const response = await api.put(`/dados_complementares/admin/relacionamentos-campos/versoes/${id}`, payload, { headers: authHeaders() });
  return response.data;
};

export const publicarVersaoRelacionamentosCamposAdmin = async (id: number) => {
  const response = await api.post(`/dados_complementares/admin/relacionamentos-campos/versoes/${id}/publicar`, {}, { headers: authHeaders() });
  return response.data;
};

export const excluirVersaoRelacionamentosCamposAdmin = async (id: number) => {
  const response = await api.delete(`/dados_complementares/admin/relacionamentos-campos/versoes/${id}`, { headers: authHeaders() });
  return response.data;
};

export const listarColunasRelacionamentoCampoAdmin = async (table: string) => {
  const response = await api.get(`/dados_complementares/admin/relacionamentos-campos/tabelas/${table}/colunas`, {
    headers: authHeaders(),
  });
  const payload = response.data;
  const data = payload && typeof payload === "object" && "data" in payload
    ? (payload as { data?: unknown }).data
    : payload;

  return data && typeof data === "object" && Array.isArray((data as { colunas?: unknown }).colunas)
    ? (data as { colunas: string[] }).colunas
    : [];
};

export const criarRelacionamentoCampoAdmin = async (payload: RelacionamentoCampoPayload) => {
  const response = await api.post("/dados_complementares/admin/relacionamentos-campos", payload, { headers: authHeaders() });
  return response.data;
};

export const atualizarRelacionamentoCampoAdmin = async (id: number, payload: RelacionamentoCampoPayload) => {
  const response = await api.put(`/dados_complementares/admin/relacionamentos-campos/${id}`, payload, { headers: authHeaders() });
  return response.data;
};

export const excluirRelacionamentoCampoAdmin = async (id: number) => {
  const response = await api.delete(`/dados_complementares/admin/relacionamentos-campos/${id}`, { headers: authHeaders() });
  return response.data;
};

export const criarCampoAdmin = async (
  basePath: "dados_complementares" | "acoes",
  table: string,
  payload: Record<string, unknown>,
) => {
  const response = await api.post(`/${basePath}/admin/${table}`, payload, { headers: authHeaders() });
  return response.data;
};

export const atualizarCampoAdmin = async (
  basePath: "dados_complementares" | "acoes",
  table: string,
  id: number,
  payload: Record<string, unknown>,
) => {
  const response = await api.put(`/${basePath}/admin/${table}/${id}`, payload, { headers: authHeaders() });
  return response.data;
};

export const excluirCampoAdmin = async (
  basePath: "dados_complementares" | "acoes",
  table: string,
  id: number,
) => {
  const response = await api.delete(`/${basePath}/admin/${table}/${id}`, { headers: authHeaders() });
  return response.data;
};

export const listarPolosRegionaisAdmin = async () => {
  const response = await api.get("/regionais/admin/polos", { headers: authHeaders() });
  return extractData<PoloRegionalAdmin>(response.data);
};

export const consultarPoloRegionalAdmin = async (id: number) => {
  const response = await api.get(`/regionais/admin/polos/${id}`, { headers: authHeaders() });
  const payload = response.data;

  if (payload && typeof payload === "object" && "data" in payload && !Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: PoloRegionalAdmin }).data;
  }

  return payload as PoloRegionalAdmin;
};

export const criarPoloRegionalAdmin = async (payload: Record<string, unknown>) => {
  const response = await api.post("/regionais/admin/polos", payload, { headers: authHeaders() });
  return response.data;
};

export const atualizarPoloRegionalAdmin = async (id: number, payload: Record<string, unknown>) => {
  const response = await api.put(`/regionais/admin/polos/${id}`, payload, { headers: authHeaders() });
  return response.data;
};

export const excluirPoloRegionalAdmin = async (id: number) => {
  const response = await api.delete(`/regionais/admin/polos/${id}`, { headers: authHeaders() });
  return response.data;
};

export const vincularMunicipiosPoloRegional = async (poloId: number, cityIds: number[]) => {
  const response = await api.post(
    `/regionais/admin/polos/${poloId}/municipios`,
    { city_ids: cityIds },
    { headers: authHeaders() },
  );
  return response.data;
};

export const desvincularMunicipiosPoloRegional = async (poloId: number, cityIds: number[]) => {
  const response = await api.delete(`/regionais/admin/polos/${poloId}/municipios`, {
    data: { city_ids: cityIds },
    headers: authHeaders(),
  });
  return response.data;
};
